import { useState, useEffect } from "react";
import { Link, Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, TooltipArrow } from "@shopmuchmore/design-system";
import {
  Menu,
  MapPin,
  ChevronDown,
  X
} from "lucide-react";
import { NavigationDrawer } from "./NavigationDrawer";
import { useLocationPicker, LocationPickerModal, geocodeAddress, DEFAULT_COORDINATES } from "../../features/local";
import { getShippingAddressesService } from "../../features/shipping/shipping.services";
import { env } from "../../config/env";

function getDistanceInKm(lat1: number, lon1: number, lat2: number, lon2: number) {
  const R = 6371; // Radius of the earth in km
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) * 
    Math.sin(dLon / 2) * Math.sin(dLon / 2); 
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)); 
  return R * c; // Distance in km
}

const quickLinks = [
  { label: "Home", href: "/" },
  { label: "About Us", href: "/about" },
  { label: "Contact Us", href: "/contact" },
  { label: "Sell on ShopMuchMore", href: `${env.merchantPlatformUrl}manual` },
];

function BottomNav() {
  const {
    address,
    latitude,
    longitude,
    lastGpsLat,
    lastGpsLng,
    setLocation,
    isAuthenticated,
    isLocationModalOpen,
    setLocationModalOpen,
  } = useLocationPicker();

  const [isTooltipDismissed, setIsTooltipDismissed] = useState(false);

  useEffect(() => {
    if (isAuthenticated && !address) {
      getShippingAddressesService().then(async addresses => {
        const defaultAddress = addresses.find(a => a.is_default) || addresses[0];
        if (defaultAddress) {
          const formatted = [defaultAddress.street_address, defaultAddress.city, defaultAddress.country].filter(Boolean).join(', ');
          let lat = (defaultAddress as any).latitude || (defaultAddress as any).lat;
          let lng = (defaultAddress as any).longitude || (defaultAddress as any).lng;
          
          if ((!lat || !lng) && formatted) {
            const coords = await geocodeAddress({ address: formatted });
            if (coords) {
              lat = coords.lat;
              lng = coords.lng;
            }
          }

          const finalLat = lat !== undefined && lat !== null ? Number(lat) : DEFAULT_COORDINATES.lat;
          const finalLng = lng !== undefined && lng !== null ? Number(lng) : DEFAULT_COORDINATES.lng;

          setLocation(finalLat, finalLng, formatted || 'Saved Address', 'saved');
        }
      }).catch(() => {
        // fail silently
      });
    }
  }, [isAuthenticated, address, setLocation]);

  const isFar = Boolean(
    latitude && longitude && lastGpsLat && lastGpsLng && 
    getDistanceInKm(latitude, longitude, lastGpsLat, lastGpsLng) > 50
  );

  const needsAddress = !address || address === "Current Location";
  const showTooltip = (isFar || needsAddress) && !isTooltipDismissed;

  return (
    <div className="bg-secondary text-background overflow-hidden">
      <div className="w-full px-4 py-1.5 sm:py-2">
        <div className="flex items-center gap-1 h-10 w-full">
          {/* Categories Menu Button with Drawer */}
          <NavigationDrawer className="flex w-auto h-auto items-center gap-1.5 px-3 py-1.5 hover:bg-background/10 transition-colors rounded shrink-0">
            <Menu className="size-5 shrink-0" />
            <span className="font-medium text-sm">Categories</span>
          </NavigationDrawer>

          {/* Divider - Desktop only */}
          <div className="h-5 w-px bg-background/30 mx-1 shrink-0 hidden lg:block" />

          {/* Quick Links - Desktop only (hidden on mobile and tablet) */}
          <div className="hidden lg:flex items-center gap-1">
            {quickLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                variant="ghost"
                className="shrink-0 whitespace-nowrap px-3 py-1.5 text-sm font-medium text-background hover:bg-background/10 hover:text-background rounded transition-colors"
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Spacer to push location selector to the right */}
          <div className="flex-1" />

          {/* Location Selector Trigger */}
          <TooltipProvider>
            <Tooltip open={showTooltip}>
              <TooltipTrigger asChild>
                <button
                  onClick={() => setLocationModalOpen(true)}
                  className="flex items-center text-left hover:bg-background/10 rounded-lg px-2 py-1.5 transition-colors shrink-0 group"
                >
                  {address && address !== "Current Location" ? (
                    <div className="flex items-center gap-1.5 text-background transition-colors">
                      <MapPin className="size-4 shrink-0 text-primary" />
                      <div className="flex flex-col justify-center">
                        <span className="text-[9px] font-bold text-background/80 uppercase tracking-widest leading-none mb-0.5">Browse your location</span>
                        <div className="flex items-center gap-1">
                          <span className="text-xs font-medium text-background truncate max-w-[160px] sm:max-w-[250px] leading-none group-hover:underline decoration-white/50 underline-offset-2">
                            {address}
                          </span>
                          <ChevronDown className="size-3.5 text-background/70" />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 text-background transition-colors">
                      <MapPin className="size-4 shrink-0 text-primary" />
                      <span className="text-xs sm:text-sm font-medium group-hover:underline decoration-white/50 underline-offset-2">Select an address</span>
                      <ChevronDown className="size-3.5 sm:size-4 text-background/70 ml-0.5" />
                    </div>
                  )}
                </button>
              </TooltipTrigger>
              {showTooltip && (
                <TooltipContent 
                  side="bottom" 
                  sideOffset={10} 
                  className={`border-none font-medium max-w-[250px] shadow-lg px-3 py-2.5 ${needsAddress ? "bg-primary text-primary-foreground" : "bg-destructive text-white"}`}
                >
                  <div className="flex items-start gap-2.5">
                    <span className="flex-1 text-left leading-snug">
                      {needsAddress 
                        ? "Please select your location to see merchants near you."
                        : "Is this correct? You seem to be quite far from your current location."}
                    </span>
                    <button 
                      onClick={() => setIsTooltipDismissed(true)} 
                      className="mt-0.5 shrink-0 hover:opacity-70 transition-opacity p-0.5 rounded-full"
                      aria-label="Dismiss message"
                    >
                      <X className="size-3.5" />
                    </button>
                  </div>
                  <TooltipArrow className={needsAddress ? "fill-primary" : "fill-destructive"} />
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
      <LocationPickerModal open={isLocationModalOpen} onOpenChange={setLocationModalOpen} />
    </div>
  );
}

export default BottomNav;
